import React, { useState, useEffect } from "react";
import Event_calander from "./Event_calander";
import AddEvent from "./AddEvent";
import BudgetPlanner from "./BudgetPlanner";
import axios from "axios";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faPlusCircle, 
  faListAlt,
  faDownload,
  faChartBar,
  faTicketAlt,
  faShoppingCart,
  faMoneyBillWave,
  faBuilding,
  faMapMarkerAlt,
  faUser,
  faPhone,
  faEnvelope,
  faStore,
  faCalendarCheck,
  faPencilAlt,
  faWallet,
  faFileExport,
  faEdit,
  faClone,
  faStoreAlt,
  faEye,
  faEyeSlash,
  faChevronDown,
  faChevronUp,
  faSearch,
  faFilter,
  faRefresh,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faTrashAlt,
  faCog,
  faSync,
  faBan,
  faSignOutAlt,
  faLock,
  faUserCircle,
  faImage,
  faSave,
  faTimes,
  faCreditCard,
  faReceipt,
  faNoteSticky,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import EditEventModal from './EditEventModal';
import "./AdminDashboard.css";
import Navbar from "./Navbar";

// API Base URL - LOCALHOST (for development)
//const API_BASE_URL = 'http://localhost/royaleheritagefiesta/api';
// For production, uncomment:
const API_BASE_URL = 'https://royaleheritagefiesta.com/royaleheritagefiesta/api';

function AdminDashboard() {
  const navigate = useNavigate();

  // ========== LOGIN STATE ==========
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [adminName, setAdminName] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // ========== DASHBOARD STATE ==========
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("bookings");

  // STATE FOR REPORT
  const [selectedDate, setSelectedDate] = useState("");
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncingEventId, setSyncingEventId] = useState(null);
  
  // State for detailed bookings
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [stallBookings, setStallBookings] = useState([]);
  const [ticketBookings, setTicketBookings] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState({});

  // State for selected budget event
  const [selectedBudgetEvent, setSelectedBudgetEvent] = useState(null);

  // State for modals
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);

  // State for edit event modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);
  
  // State for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // State for cancel booking modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  
  // State for edit booking modal
  const [showEditBookingModal, setShowEditBookingModal] = useState(false);
  const [bookingToEdit, setBookingToEdit] = useState(null);
  const [editingBooking, setEditingBooking] = useState(false);
  const [editFormData, setEditFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    customerCompany: '',
    customerRequirements: '',
    stallNumber: '',
    stallPrice: '',
    discount: '',
    finalPrice: '',
    paymentMethod: 'CASH',
    paymentStatus: 'PAID',
    bookingStatus: 'CONFIRMED'
  });

  // ========== LOGIN FUNCTION ==========
  const handleLogin = async () => {
    setLoginError("");
    
    if (!loginUsername.trim()) {
      setLoginError("Please enter username");
      return;
    }
    
    if (!loginPassword.trim()) {
      setLoginError("Please enter password");
      return;
    }
    
    setLoginLoading(true);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/admin-login.php`, {
        username: loginUsername.trim(),
        password: loginPassword.trim()
      });
      
      console.log("Login response:", response.data);
      
      if (response.data.success) {
        setIsLoggedIn(true);
        setAdminName(response.data.user?.username || loginUsername);
        sessionStorage.setItem('adminLoggedIn', 'true');
        sessionStorage.setItem('adminUsername', response.data.user?.username || loginUsername);
      } else {
        setLoginError(response.data.error || "Invalid credentials");
      }
    } catch (error) {
      console.error("Login error:", error);
      setLoginError(error.response?.data?.error || "Connection failed. Please try again.");
    } finally {
      setLoginLoading(false);
    }
  };

  // ========== LOGOUT FUNCTION ==========
  const handleLogout = () => {
    setIsLoggedIn(false);
    setLoginUsername("");
    setLoginPassword("");
    setAdminName("");
    sessionStorage.removeItem('adminLoggedIn');
    sessionStorage.removeItem('adminUsername');
  };

  // ========== CHECK SESSION ON LOAD ==========
  useEffect(() => {
    const loggedIn = sessionStorage.getItem('adminLoggedIn');
    const username = sessionStorage.getItem('adminUsername');
    if (loggedIn === 'true') {
      setIsLoggedIn(true);
      setAdminName(username || 'Admin');
    }
  }, []);

  // ========== FETCH ALL EVENTS (Only when logged in) ==========
  useEffect(() => {
    if (isLoggedIn) {
      fetchAllEvents();
    }
  }, [isLoggedIn]);

  const fetchAllEvents = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/events.php?all`);
      console.log("Fetched events:", response.data);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  // SYNC STALLS FUNCTION
  const syncStallsForEvent = async (eventId) => {
    setSyncingEventId(eventId);
    try {
      console.log("Syncing stalls for event:", eventId);
      const response = await axios.post(`${API_BASE_URL}/custom-layout.php?sync=true&eventId=${eventId}`);
      console.log("Sync response:", response.data);
      
      if (response.data.success) {
        alert(`✅ Successfully synced stalls!`);
        if (selectedDate) {
          await fetchReport();
        }
      } else {
        alert("Sync failed: " + response.data.message);
      }
    } catch (error) {
      console.error("Error syncing stalls:", error);
      alert("Error syncing stalls: " + (error.response?.data?.message || error.message));
    } finally {
      setSyncingEventId(null);
    }
  };

  // FETCH REPORT BY DATE
  const fetchReport = async () => {
    if (!selectedDate) {
      alert("Please select a date");
      return;
    }

    setLoading(true);
    try {
      console.log("Fetching report for date:", selectedDate);
      console.log("API URL:", `${API_BASE_URL}/events.php?date=${selectedDate}`);
      
      const eventsRes = await axios.get(`${API_BASE_URL}/events.php?date=${selectedDate}`);
      const eventsList = eventsRes.data;
      console.log("Events found for date:", eventsList);
      
      if (!eventsList || eventsList.length === 0) {
        setReportData([]);
        alert(`No events found for date: ${selectedDate}`);
        setLoading(false);
        return;
      }
      
      const enrichedEvents = await Promise.all(eventsList.map(async (event) => {
        try {
          console.log(`Fetching bookings for event: ${event.eventName} (ID: ${event.eventId})`);
          
          const bookingsRes = await axios.get(`${API_BASE_URL}/stall-bookings.php?eventId=${event.eventId}`);
          console.log(`Bookings for event ${event.eventId}:`, bookingsRes.data);
          
          let bookings = [];
          if (bookingsRes.data && bookingsRes.data.success && bookingsRes.data.bookings) {
            bookings = bookingsRes.data.bookings;
          } else if (bookingsRes.data && Array.isArray(bookingsRes.data)) {
            bookings = bookingsRes.data;
          }
          
          console.log(`Found ${bookings.length} stall bookings for event ${event.eventId}`);
          
          let ticketBookingsCount = 0;
          let ticketRevenue = 0;
          try {
            const ticketRes = await axios.get(`${API_BASE_URL}/ticket-bookings.php?eventId=${event.eventId}`);
            if (ticketRes.data && Array.isArray(ticketRes.data)) {
              ticketBookingsCount = ticketRes.data.length;
              ticketRevenue = ticketRes.data.reduce((sum, t) => sum + (t.totalPrice || t.price || 0), 0);
            }
          } catch (err) {
            console.log("No ticket bookings found");
          }
          
          let totalStalls = 0;
          if (event.isCustomLayout && event.customLayoutData) {
            try {
              const layoutData = typeof event.customLayoutData === 'string' 
                ? JSON.parse(event.customLayoutData) 
                : event.customLayoutData;
              totalStalls = layoutData.stalls?.length || 0;
            } catch (e) {
              totalStalls = 50;
            }
          } else {
            switch(event.stallPattern) {
              case "full": totalStalls = 50; break;
              case "ushape": totalStalls = 50; break;
              case "twosides": totalStalls = 50; break;
              default: totalStalls = 50;
            }
          }
          
          const bookedStalls = bookings.length;
          const stallRevenue = bookings.reduce((sum, b) => sum + (parseFloat(b.finalPrice) || parseFloat(b.stallPrice) || 0), 0);
          
          let totalTickets = 0;
          if (event.hasSeating) {
            totalTickets = event.totalSeats || 0;
          } else {
            totalTickets = event.standingCapacity || 0;
          }
          
          return {
            ...event,
            totalStalls: totalStalls,
            bookedStalls: bookedStalls,
            availableStalls: totalStalls - bookedStalls,
            stallRevenue: stallRevenue,
            bookedTickets: ticketBookingsCount,
            availableTickets: totalTickets - ticketBookingsCount,
            ticketRevenue: ticketRevenue,
            totalRevenue: stallRevenue + ticketRevenue
          };
        } catch (err) {
          console.error(`Error processing event ${event.eventId}:`, err);
          return {
            ...event,
            totalStalls: 0,
            bookedStalls: 0,
            availableStalls: 0,
            stallRevenue: 0,
            bookedTickets: 0,
            availableTickets: 0,
            ticketRevenue: 0,
            totalRevenue: 0
          };
        }
      }));
      
      console.log("Enriched events:", enrichedEvents);
      setReportData(enrichedEvents);
      
    } catch (error) {
      console.error("Error fetching report:", error);
      alert("Error fetching report: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // DELETE EVENT FUNCTION
  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;
    
    setDeleting(true);
    try {
      console.log("Deleting event ID:", eventToDelete.eventId);
      
      const response = await axios.delete(`${API_BASE_URL}/events.php?eventId=${eventToDelete.eventId}`);
      
      console.log("Delete response:", response.data);
      
      if (response.data.success) {
        alert(`✅ Event "${eventToDelete.eventName}" deleted successfully!`);
        
        await fetchAllEvents();
        
        if (selectedDate) {
          await fetchReport();
        }
        
        setShowDeleteModal(false);
        setEventToDelete(null);
      } else {
        throw new Error(response.data.message || "Delete failed");
      }
      
    } catch (error) {
      console.error("Error deleting event:", error);
      alert("Failed to delete event: " + (error.response?.data?.message || error.message));
    } finally {
      setDeleting(false);
    }
  };
  
  // CANCEL STALL BOOKING FUNCTION
  const handleCancelStallBooking = (eventId, stallNumber, customerName, bookingId) => {
    setBookingToCancel({ eventId, stallNumber, customerName, type: 'stall', bookingId });
    setShowCancelModal(true);
  };
  
  // CANCEL TICKET BOOKING FUNCTION
  const handleCancelTicketBooking = (eventId, bookingId, customerName) => {
    setBookingToCancel({ eventId, bookingId, customerName, type: 'ticket' });
    setShowCancelModal(true);
  };
  
  // CONFIRM CANCEL BOOKING
  const confirmCancelBooking = async () => {
    if (!bookingToCancel) return;
    
    setCancelling(true);
    try {
      if (bookingToCancel.type === 'stall') {
        let response;
        if (bookingToCancel.bookingId) {
          response = await axios.delete(`${API_BASE_URL}/stall-bookings.php?bookingId=${bookingToCancel.bookingId}`);
        } else {
          response = await axios.delete(`${API_BASE_URL}/stall-bookings.php?eventId=${bookingToCancel.eventId}&stallNumber=${bookingToCancel.stallNumber}`);
        }
        
        if (response.data.success) {
          alert(`✅ Stall booking for "${bookingToCancel.customerName}" has been cancelled successfully!`);
          
          if (selectedEvent && selectedEvent.eventId === bookingToCancel.eventId) {
            await fetchEventDetails(bookingToCancel.eventId);
          }
          if (selectedDate) {
            await fetchReport();
          }
          await fetchAllEvents();
        }
      } else if (bookingToCancel.type === 'ticket') {
        const response = await axios.delete(`${API_BASE_URL}/ticket-bookings.php?bookingId=${bookingToCancel.bookingId}`);
        
        if (response.data.success) {
          alert(`✅ Ticket booking for "${bookingToCancel.customerName}" has been cancelled successfully!`);
          
          if (selectedEvent && selectedEvent.eventId === bookingToCancel.eventId) {
            await fetchEventDetails(bookingToCancel.eventId);
          }
          if (selectedDate) {
            await fetchReport();
          }
          await fetchAllEvents();
        }
      }
      
      setShowCancelModal(false);
      setBookingToCancel(null);
      
    } catch (error) {
      console.error("Error cancelling booking:", error);
      alert("Failed to cancel booking: " + (error.response?.data?.error || error.message));
    } finally {
      setCancelling(false);
    }
  };
  
  // EDIT BOOKING FUNCTION - Open edit modal
  const handleEditBooking = (booking) => {
    setBookingToEdit(booking);
    setEditFormData({
      customerName: booking.customer?.name || '',
      customerPhone: booking.customer?.phone || '',
      customerEmail: booking.customer?.email || '',
      customerCompany: booking.customerCompany || '',
      customerRequirements: booking.customerRequirements || '',
      stallNumber: booking.stallNumber,
      stallPrice: booking.originalPrice,
      discount: booking.discount,
      finalPrice: booking.finalPrice,
      paymentMethod: booking.paymentMethod || 'CASH',
      paymentStatus: booking.paymentStatus || 'PAID',
      bookingStatus: booking.status || 'CONFIRMED'
    });
    setShowEditBookingModal(true);
  };
  
  // UPDATE FINAL PRICE when discount changes in edit form
  const handleEditDiscountChange = (e) => {
    const discountAmount = parseFloat(e.target.value) || 0;
    const newFinalPrice = Math.max(0, editFormData.stallPrice - discountAmount);
    setEditFormData({
      ...editFormData,
      discount: discountAmount,
      finalPrice: newFinalPrice
    });
  };
  
  // SAVE EDITED BOOKING
  const saveEditedBooking = async () => {
    if (!bookingToEdit) return;
    
    setEditingBooking(true);
    try {
      const updateData = {
        bookingId: bookingToEdit.bookingId,
        customerName: editFormData.customerName,
        customerPhone: editFormData.customerPhone,
        customerEmail: editFormData.customerEmail,
        customerCompany: editFormData.customerCompany,
        customerRequirements: editFormData.customerRequirements,
        stallNumber: editFormData.stallNumber,
        stallPrice: editFormData.stallPrice,
        discount: editFormData.discount,
        finalPrice: editFormData.finalPrice,
        paymentMethod: editFormData.paymentMethod,
        paymentStatus: editFormData.paymentStatus,
        bookingStatus: editFormData.bookingStatus
      };
      
      console.log("Updating booking:", updateData);
      
      const response = await axios.put(`${API_BASE_URL}/stall-bookings.php`, updateData);
      
      if (response.data.success) {
        alert(`✅ Booking #${bookingToEdit.bookingId} updated successfully!`);
        
        if (selectedEvent && selectedEvent.eventId) {
          await fetchEventDetails(selectedEvent.eventId);
        }
        if (selectedDate) {
          await fetchReport();
        }
        await fetchAllEvents();
        
        setShowEditBookingModal(false);
        setBookingToEdit(null);
      } else {
        throw new Error(response.data.error || "Update failed");
      }
      
    } catch (error) {
      console.error("Error updating booking:", error);
      alert("Failed to update booking: " + (error.response?.data?.error || error.message));
    } finally {
      setEditingBooking(false);
    }
  };

  const handleNavigateToCustomLayout = () => {
    navigate('/admin/layout-builder', { 
      state: { 
        fromAddEvent: true,
        isCustomLayout: true
      } 
    });
  };

  // FETCH EVENT DETAILS - Updated to include bookedBy
  const fetchEventDetails = async (eventId) => {
    setLoadingDetails(true);
    try {
      console.log("========== FETCHING BOOKING DETAILS ==========");
      console.log("Event ID:", eventId);
      
      let allBookings = [];
      try {
        const bookingsRes = await axios.get(`${API_BASE_URL}/stall-bookings.php?eventId=${eventId}`);
        console.log("Raw bookings from API:", bookingsRes.data);
        
        if (bookingsRes.data && bookingsRes.data.success && bookingsRes.data.bookings) {
          allBookings = bookingsRes.data.bookings;
        } else if (bookingsRes.data && Array.isArray(bookingsRes.data)) {
          allBookings = bookingsRes.data;
        }
      } catch (bookingError) {
        console.error("Error fetching bookings:", bookingError);
        allBookings = [];
      }
      
      console.log("Total bookings found:", allBookings.length);
      
      const stallBookingsData = allBookings.map(booking => {
        const originalPrice = parseFloat(booking.stallPrice) || 0;
        const discountAmount = parseFloat(booking.discount) || 0;
        const calculatedFinalPrice = booking.finalPrice || (originalPrice - discountAmount);
        
        return {
          stallId: booking.stallNumber,
          stallNumber: booking.stallNumber,
          customer: {
            name: booking.customerName || 'N/A',
            phone: booking.customerPhone || 'N/A',
            email: booking.customerEmail || ''
          },
          customerCompany: booking.customerCompany || '',
          customerRequirements: booking.customerRequirements || '',
          stallDescription: booking.stallDescription || '',
          bookedBy: booking.bookedBy || '',
          bookedByPhone: booking.bookedByPhone || '',
          paymentMethod: booking.paymentMethod || 'CASH',
          paymentStatus: booking.paymentStatus || 'PAID',
          bookingDate: booking.bookingDate,
          originalPrice: originalPrice,
          discount: discountAmount,
          finalPrice: calculatedFinalPrice,
          paymentReceipt: booking.paymentReceipt,
          status: booking.bookingStatus || 'CONFIRMED',
          bookingId: booking.bookingId,
          paymentId: booking.paymentId || null,
          orderId: booking.orderId || null,
          receiptEventName: booking.eventName_receipt || null,
          receiptEventDate: booking.eventDate_receipt || null,
          receiptEventPlace: booking.eventPlace_receipt || null,
          receiptItemType: booking.itemType || 'Stall',
          receiptItemNumber: booking.itemNumber || booking.stallNumber,
          receiptBookingTime: booking.bookingTime || booking.bookingDate
        };
      });
      
      setStallBookings(stallBookingsData);
      console.log("✅ Final stall bookings count:", stallBookingsData.length);
      
      try {
        console.log(`Fetching ticket bookings...`);
        const ticketResponse = await axios.get(`${API_BASE_URL}/ticket-bookings.php?eventId=${eventId}`);
        console.log("Raw ticket response:", ticketResponse.data);
        
        let ticketBookingsData = [];
        
        if (ticketResponse.data && Array.isArray(ticketResponse.data)) {
          ticketBookingsData = ticketResponse.data.map(ticket => ({
            seatNo: ticket.seatNo || (ticket.ticketType === 'standing' ? 'General Admission' : 'N/A'),
            customerName: ticket.customerName || 'N/A',
            phoneNumber: ticket.customerPhone || 'N/A',
            email: ticket.customerEmail || '',
            bookingTime: ticket.bookingTime || new Date().toISOString(),
            price: ticket.price || 1000,
            status: ticket.status || 'CONFIRMED',
            ticketType: ticket.ticketType || 'seated',
            quantity: ticket.quantity || 1,
            totalPrice: ticket.totalPrice || (ticket.price || 1000) * (ticket.quantity || 1),
            bookingId: ticket.bookingId
          }));
        }
        
        setTicketBookings(ticketBookingsData);
        console.log("✅ Ticket bookings count:", ticketBookingsData.length);
        
      } catch (ticketError) {
        console.error("Error fetching tickets:", ticketError);
        setTicketBookings([]);
      }
      
    } catch (error) {
      console.error("Error in fetchEventDetails:", error);
      setStallBookings([]);
      setTicketBookings([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const toggleEventExpanded = (eventId) => {
    setExpandedEvents(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  const addEvent = (eventDetails) => {
    fetchAllEvents();
    setView("calendar");
  };

  const handleEditLayout = (event) => {
    const eventData = {
      eventId: event.eventId,
      name: event.eventName,
      date: event.eventDate,
      spansaour: event.spansour,
      venue: event.place,
      budget: event.budget,
      highlights: event.highlights,
      stallPrice: event.stallPrice,
      ticketPrice: event.ticketPrice,
      standingCapacity: event.standingCapacity,
      hasSeating: event.hasSeating,
      seatingType: event.seatingType,
      seatRows: event.seatRows,
      seatColumns: event.seatColumns,
      totalSeats: event.totalSeats,
      showEntrance: event.showEntrance,
      showExit: event.showExit,
      showStage: event.showStage,
      showParking: event.showParking,
      parkingZones: event.parkingZones,
      hasVIPParking: event.hasVIPParking,
      showFoodCourt: event.showFoodCourt,
      showRestrooms: event.showRestrooms,
      showFirstAid: event.showFirstAid,
      isCustomLayout: event.isCustomLayout,
      isEditMode: true
    };
    
    sessionStorage.setItem('pendingEventData', JSON.stringify(eventData));
    
    navigate('/admin/layout-builder', { 
      state: { 
        fromAddEvent: true,
        eventName: event.eventName,
        eventId: event.eventId,
        isEditMode: true
      } 
    });
  };

  const handleDuplicateLayout = (event) => {
    const eventData = {
      name: `${event.eventName} (Copy)`,
      date: event.eventDate,
      spansaour: event.spansour,
      venue: event.place,
      budget: event.budget,
      highlights: event.highlights,
      stallPrice: event.stallPrice,
      ticketPrice: event.ticketPrice,
      standingCapacity: event.standingCapacity,
      hasSeating: event.hasSeating,
      seatingType: event.seatingType,
      seatRows: event.seatRows,
      seatColumns: event.seatColumns,
      totalSeats: event.totalSeats,
      showEntrance: event.showEntrance,
      showExit: event.showExit,
      showStage: event.showStage,
      showParking: event.showParking,
      parkingZones: event.parkingZones,
      hasVIPParking: event.hasVIPParking,
      showFoodCourt: event.showFoodCourt,
      showRestrooms: event.showRestrooms,
      showFirstAid: event.showFirstAid,
      isCustomLayout: event.isCustomLayout,
      isEditMode: false,
      duplicateFrom: event.eventId
    };
    
    sessionStorage.setItem('pendingEventData', JSON.stringify(eventData));
    
    navigate('/admin/layout-builder', { 
      state: { 
        fromAddEvent: true,
        eventName: `${event.eventName} (Copy)`,
        isEditMode: false,
        duplicateFrom: event.eventId
      } 
    });
  };

  const handleSaveEditedEvent = async (updatedEvent) => {
    try {
      console.log("Saving updated event:", updatedEvent);
      
      const eventData = {
        eventId: updatedEvent.eventId,
        eventName: updatedEvent.eventName,
        eventDate: updatedEvent.eventDate,
        eventType: updatedEvent.eventType || null,
        spansour: updatedEvent.spansour,
        place: updatedEvent.place,
        budget: updatedEvent.budget,
        highlights: updatedEvent.highlights,
        stallPrice: updatedEvent.stallPrice,
        ticketPrice: updatedEvent.ticketPrice,
        standingCapacity: updatedEvent.standingCapacity || 0,
        stallPattern: updatedEvent.stallPattern || 'full',
        isCustomLayout: updatedEvent.isCustomLayout || 0,
        hasSeating: updatedEvent.hasSeating,
        seatingType: updatedEvent.seatingType,
        seatRows: updatedEvent.seatRows,
        seatColumns: updatedEvent.seatColumns,
        totalSeats: updatedEvent.totalSeats,
        showEntrance: updatedEvent.showEntrance,
        showExit: updatedEvent.showExit,
        showStage: updatedEvent.showStage,
        showParking: updatedEvent.showParking,
        parkingZones: updatedEvent.parkingZones,
        hasVIPParking: updatedEvent.hasVIPParking,
        showFoodCourt: updatedEvent.showFoodCourt,
        showRestrooms: updatedEvent.showRestrooms,
        showFirstAid: updatedEvent.showFirstAid
      };
      
      const response = await axios.put(
        `${API_BASE_URL}/events.php`,
        eventData
      );
      
      console.log("Event updated successfully:", response.data);
      
      if (response.data.success) {
        alert("✅ Event details updated successfully!");
        
        await fetchAllEvents();
        
        if (selectedDate) {
          await fetchReport();
        }
        
        if (expandedEvents[updatedEvent.eventId]) {
          await fetchEventDetails(updatedEvent.eventId);
        }
        
        setShowEditModal(false);
        setEventToEdit(null);
      } else {
        throw new Error(response.data.error || "Failed to update event");
      }
      
    } catch (error) {
      console.error("Error updating event:", error);
      alert("Failed to update event: " + (error.response?.data?.error || error.message));
      throw error;
    }
  };

  const exportToCSV = () => {
    if (reportData.length === 0) return;
    
    const headers = ['Event Name', 'Event ID', 'Date', 'Sponsor', 'Place', 'Total Stalls', 'Booked Stalls', 'Available Stalls', 'Stall Revenue', 'Total Tickets', 'Booked Tickets', 'Available Tickets', 'Ticket Revenue', 'Total Revenue'];
    
    const csvData = reportData.map(event => [
      event.eventName,
      event.eventId,
      event.eventDate,
      event.spansour,
      event.place || 'N/A',
      event.totalStalls,
      event.bookedStalls,
      event.availableStalls,
      event.stallRevenue || 0,
      event.totalTickets,
      event.bookedTickets,
      event.availableTickets,
      event.ticketRevenue || 0,
      event.totalRevenue || 0
    ]);
    
    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${selectedDate}.csv`;
    a.click();
  };

  const calculateSummary = () => {
    if (reportData.length === 0) return null;
    
    return reportData.reduce((acc, event) => {
      return {
        totalStalls: acc.totalStalls + (event.totalStalls || 0),
        bookedStalls: acc.bookedStalls + (event.bookedStalls || 0),
        totalTickets: acc.totalTickets + (event.totalTickets || 0),
        bookedTickets: acc.bookedTickets + (event.bookedTickets || 0),
        totalRevenue: acc.totalRevenue + (event.totalRevenue || 0),
        stallRevenue: acc.stallRevenue + (event.stallRevenue || 0),
        ticketRevenue: acc.ticketRevenue + (event.ticketRevenue || 0)
      };
    }, {
      totalStalls: 0,
      bookedStalls: 0,
      totalTickets: 0,
      bookedTickets: 0,
      totalRevenue: 0,
      stallRevenue: 0,
      ticketRevenue: 0
    });
  };

  const summary = calculateSummary();

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  useEffect(() => {
    if (showBudgetModal || showExpenseModal || showEditModal || showDeleteModal || showCancelModal || showEditBookingModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showBudgetModal, showExpenseModal, showEditModal, showDeleteModal, showCancelModal, showEditBookingModal]);

  // ========== IF NOT LOGGED IN, SHOW LOGIN FORM ==========
  if (!isLoggedIn) {
    return (
      <>
        <Navbar />
        <div style={{
          minHeight: 'calc(100vh - 200px)',
          background: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          borderLeft: '29.5px solid #000',
          borderRight: '29.5px solid #000'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '40px',
            width: '100%',
            maxWidth: '420px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '30px' }}>
              <div style={{
                width: '70px',
                height: '70px',
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <FontAwesomeIcon icon={faChartBar} style={{ fontSize: '32px', color: '#b8860b' }} />
              </div>
              <h2 style={{ color: '#333', marginBottom: '8px', fontFamily: "'Cinzel', serif" }}>Admin Login</h2>
              <p style={{ color: '#666', fontSize: '14px' }}>Enter your credentials to access dashboard</p>
            </div>
            
            {loginError && (
              <div style={{
                background: '#fee2e2',
                color: '#dc2626',
                padding: '10px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '14px',
                textAlign: 'center'
              }}>
                {loginError}
              </div>
            )}
            
            <div style={{ marginBottom: '20px', textAlign: 'left' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>Username</label>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faUserCircle} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                <input
                  type="text"
                  placeholder="Enter username"
                  value={loginUsername}
                  onChange={e => setLoginUsername(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                  style={{
                    width: '100%',
                    padding: '12px 15px 12px 40px',
                    border: '1px solid #ddd',
                    borderRadius: '10px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#b8860b'}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                />
              </div>
            </div>

            <div style={{ marginBottom: '25px', textAlign: 'left' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>Password</label>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faLock} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={loginPassword}
                  onChange={e => setLoginPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                  style={{
                    width: '100%',
                    padding: '12px 15px 12px 40px',
                    border: '1px solid #ddd',
                    borderRadius: '10px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#b8860b'}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#999'
                  }}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            <button 
              onClick={handleLogin}
              disabled={loginLoading}
              style={{
                width: '100%',
                padding: '12px',
                background: '#b8860b',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loginLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s',
                opacity: loginLoading ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!loginLoading) e.target.style.background = '#9b6e09';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#b8860b';
              }}
            >
              {loginLoading ? (
                <>
                  <span className="spinner" style={{ display: 'inline-block', width: '16px', height: '16px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: '8px' }}></span>
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </button>
            
            <div style={{ marginTop: '20px', fontSize: '12px', color: '#999' }}>
              
            </div>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
        <Footer />
      </>
    );
  }

  // ========== SHOW DASHBOARD CONTENT WHEN LOGGED IN ==========
  return (
    <div className="admin-dashboard">
      {/* Dashboard Header with Logout */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '2rem',
        marginBottom: '2rem',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{ color: '#fff', fontFamily: "'Cinzel', serif", fontSize: 'clamp(1.2rem, 5vw, 1.8rem)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FontAwesomeIcon icon={faChartBar} style={{ color: '#b8860b' }} />
            Admin Dashboard
          </h1>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 'clamp(0.75rem, 3vw, 0.9rem)' }}>Welcome back, {adminName}! Manage your events, view reports, and plan budgets</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ background: 'rgba(255, 255, 255, 0.1)', padding: '0.5rem 1rem', borderRadius: '30px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Total Events</span>
            <span style={{ color: '#b8860b', fontWeight: 700 }}>{events.length}</span>
          </div>
          <div style={{ background: 'rgba(255, 255, 255, 0.1)', padding: '0.5rem 1rem', borderRadius: '30px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Reports</span>
            <span style={{ color: '#b8860b', fontWeight: 700 }}>{reportData.length}</span>
          </div>
          <button 
            onClick={handleLogout}
            style={{
              background: '#dc3545',
              border: 'none',
              padding: '0.5rem 1.2rem',
              borderRadius: '30px',
              color: '#fff',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '14px',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => e.target.style.background = '#c82333'}
            onMouseLeave={(e) => e.target.style.background = '#dc3545'}
          >
            <FontAwesomeIcon icon={faSignOutAlt} />
            Logout
          </button>
        </div>
      </div>

      <div className="quick-actions-bar">
        <button className="quick-action-btn webpage-btn" onClick={() => navigate('/webpage-editor/home')}>
          <FontAwesomeIcon icon={faEdit} /> Edit Web Pages
        </button>
        <button className="quick-action-btn refresh-btn" onClick={() => { fetchAllEvents(); if (selectedDate) fetchReport(); }}>
          <FontAwesomeIcon icon={faRefresh} /> Refresh Data
        </button>
      </div>

      <div className="nav-tabs-container">
        <button className={`nav-tab ${view === "bookings" ? "active" : ""}`} onClick={() => setView("bookings")}>
          <FontAwesomeIcon icon={faListAlt} /> Booking Details
        </button>
        <button className={`nav-tab ${view === "calendar" ? "active" : ""}`} onClick={() => setView("calendar")}>
          <FontAwesomeIcon icon={faCalendarAlt} /> Event Calendar
        </button>
        <button className={`nav-tab ${view === "addEvent" ? "active" : ""}`} onClick={() => setView("addEvent")}>
          <FontAwesomeIcon icon={faPlusCircle} /> Add Event
        </button>
      </div>

      {view === "bookings" && (
        <div className="bookings-section">
          <div className="section-header">
            <h2><FontAwesomeIcon icon={faChartBar} className="section-icon" /> Booking Report By Date</h2>
          </div>

          <div className="date-selection-card">
            <div className="date-input-group">
              <label>Select Date</label>
              <div className="date-input-wrapper">
                <input type="date" className="date-input" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
                <button className="btn-primary" onClick={fetchReport} disabled={loading}>
                  {loading ? (<><span className="spinner"></span> Loading...</>) : (<><FontAwesomeIcon icon={faChartBar} /> Get Report</>)}
                </button>
                {reportData.length > 0 && (<button className="btn-export" onClick={exportToCSV}><FontAwesomeIcon icon={faDownload} /> Export CSV</button>)}
              </div>
            </div>
          </div>

          {reportData.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <h3>No Data Available</h3>
              <p>No booking data available for {selectedDate || "selected date"}</p>
            </div>
          ) : (
            <>
              {summary && (
                <div className="summary-cards">
                  <div className="summary-card stalls">
                    <div className="card-icon"><FontAwesomeIcon icon={faBuilding} /></div>
                    <div className="card-content">
                      <h3>Stall Statistics</h3>
                      <div className="stats-row" style={{color:"black", fontWeight:"700"}}>
                        <span>Total: <strong>{summary.totalStalls}</strong></span>
                        <span>Booked: <strong style={{ color: '#dc3545' }}>{summary.bookedStalls}</strong></span>
                        <span>Available: <strong style={{ color: '#28a745' }}>{summary.totalStalls - summary.bookedStalls}</strong></span>
                      </div>
                      <div className="progress-bar-container">
                        <div className="progress-fill booked" style={{ width: summary.totalStalls > 0 ? `${(summary.bookedStalls / summary.totalStalls) * 100}%` : '0%' }}></div>
                        <div className="progress-fill available" style={{ width: summary.totalStalls > 0 ? `${((summary.totalStalls - summary.bookedStalls) / summary.totalStalls) * 100}%` : '0%' }}></div>
                      </div>
                    </div>
                  </div>

                  <div className="summary-card tickets">
                    <div className="card-icon"><FontAwesomeIcon icon={faTicketAlt} /></div>
                    <div className="card-content">
                      <h3>Ticket Statistics</h3>
                      <div className="stats-row">
                        <span>Total: <strong>{summary.totalTickets}</strong></span>
                        <span>Booked: <strong style={{ color: '#dc3545' }}>{summary.bookedTickets}</strong></span>
                        <span>Available: <strong style={{ color: '#28a745' }}>{summary.totalTickets - summary.bookedTickets}</strong></span>
                      </div>
                      <div className="progress-bar-container">
                        <div className="progress-fill booked" style={{ width: summary.totalTickets > 0 ? `${(summary.bookedTickets / summary.totalTickets) * 100}%` : '0%' }}></div>
                        <div className="progress-fill available" style={{ width: summary.totalTickets > 0 ? `${((summary.totalTickets - summary.bookedTickets) / summary.totalTickets) * 100}%` : '0%' }}></div>
                      </div>
                    </div>
                  </div>

                  <div className="summary-card revenue">
                    <div className="card-icon"><FontAwesomeIcon icon={faMoneyBillWave} /></div>
                    <div className="card-content">
                      <h3>Revenue Summary</h3>
                      <div className="revenue-stats">
                        <div className="revenue-item"><span>Stall Revenue</span><strong>₹{summary.stallRevenue.toLocaleString()}</strong></div>
                        <div className="revenue-item"><span>Ticket Revenue</span><strong>₹{summary.ticketRevenue.toLocaleString()}</strong></div>
                        <div className="revenue-total"><span>Total Revenue</span><strong>₹{summary.totalRevenue.toLocaleString()}</strong></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="date-header">
                <h3><FontAwesomeIcon icon={faCalendarAlt} className="date-icon" /> Bookings for {selectedDate}</h3>
                <span className="event-count">{reportData.length} Event(s)</span>
              </div>

              {reportData.map((event, index) => (
                <div key={index} className="event-report-card">
                  <div className="event-card-header">
                    <div className="event-title"><h4>{event.eventName}</h4><span className="event-id">ID: {event.eventId}</span></div>
                    <div className="event-meta">
                      <span><FontAwesomeIcon icon={faBuilding} /> {event.spansour}</span>
                      <span><FontAwesomeIcon icon={faMapMarkerAlt} /> {event.place || 'N/A'}</span>
                    </div>
                    <div className="event-actions">
                      <button className="action-btn book-stalls" onClick={() => navigate('/admin/stall-booking', { state: { eventId: event.eventId } })}><FontAwesomeIcon icon={faStoreAlt} /> Book Stalls</button>
                      <button className="action-btn edit-details" onClick={() => { setEventToEdit(event); setShowEditModal(true); }}><FontAwesomeIcon icon={faPencilAlt} /> Edit Details</button>
                      <button className="action-btn delete-event" onClick={() => { setEventToDelete(event); setShowDeleteModal(true); }}><FontAwesomeIcon icon={faTrashAlt} /> Delete Event</button>
                      {event.isCustomLayout && (
                        <>
                          <button className="action-btn edit-layout" onClick={() => handleEditLayout(event)}><FontAwesomeIcon icon={faEdit} /> Edit Layout</button>
                          <button className="action-btn duplicate-layout" onClick={() => handleDuplicateLayout(event)}><FontAwesomeIcon icon={faClone} /> Duplicate</button>
                          <button className="action-btn sync-stalls" onClick={() => syncStallsForEvent(event.eventId)} disabled={syncingEventId === event.eventId} style={{background: '#8b5cf6'}}><FontAwesomeIcon icon={faSync} spin={syncingEventId === event.eventId} /> {syncingEventId === event.eventId ? 'Syncing...' : 'Sync Stalls'}</button>
                        </>
                      )}
                      <button className="action-btn view-details" onClick={() => { setSelectedEvent(event); fetchEventDetails(event.eventId); toggleEventExpanded(event.eventId); }}><FontAwesomeIcon icon={expandedEvents[event.eventId] ? faEyeSlash : faEye} /> {expandedEvents[event.eventId] ? 'Hide' : 'View'} Bookings</button>
                    </div>
                  </div>

                  <div className="event-card-body">
                    <div className="stat-section">
                      <div className="stat-header"><FontAwesomeIcon icon={faShoppingCart} className="stat-icon" /><h5>Stall Booking {event.isCustomLayout && <span className="custom-badge">Custom Layout</span>}</h5></div>
                      <div className="stat-grid">
                        <div className="stat-item"><span className="stat-label">Total Stalls</span><span className="" style={{ color: '#000', fontWeight:"700"}}>{event.totalStalls}</span></div>
                        <div className="stat-item"><span className="stat-label booked">Booked</span><span className=" booked" style={{ color: '#dc3545',fontWeight:"700" }}>{event.bookedStalls}</span></div>
                        <div className="stat-item"><span className="stat-label available">Available</span><span className="available" style={{ color: '#28a745',fontWeight:"700" }}>{event.availableStalls}</span></div>
                        <div className="stat-item"><span className="stat-label">Occupancy</span><span className=" percent" style={{ color: '#000' ,fontWeight:"700"}}>{event.totalStalls > 0 ? ((event.bookedStalls / event.totalStalls) * 100).toFixed(1) : '0.0'}%</span></div>
                      </div>
                      <div className="progress-bar-container"><div className="progress-fill booked" style={{ width: event.totalStalls > 0 ? `${(event.bookedStalls / event.totalStalls) * 100}%` : '0%' }}></div></div>
                    </div>

                    <div className="stat-section">
                      <div className="stat-header"><FontAwesomeIcon icon={faTicketAlt} className="stat-icon" /><h5>Ticket Sales</h5></div>
                      <div className="stat-grid">
                        <div className="stat-item"><span className="stat-label">Total Seats</span><span className="" style={{ color: '#000',fontWeight:"700" }}>{event.hasSeating ? event.totalSeats : event.standingCapacity}</span></div>
                        <div className="stat-item"><span className="stat-label booked">Booked</span><span className=" booked" style={{ color: '#dc3545',fontWeight:"700" }}>{event.bookedTickets}</span></div>
                        <div className="stat-item"><span className="stat-label available">Available</span><span className=" available" style={{ color: '#28a745',fontWeight:"700" }}>{event.hasSeating ? (event.totalSeats - (event.bookedTickets || 0)) : (event.standingCapacity - (event.bookedTickets || 0))}</span></div>
                        <div className="stat-item"><span className="stat-label">Occupancy</span><span className=" percent" style={{ color: '#000',fontWeight:"700" }}>{(() => { const total = event.hasSeating ? event.totalSeats : event.standingCapacity; const booked = event.bookedTickets || 0; return total > 0 ? ((booked / total) * 100).toFixed(1) : '0.0'; })()}%</span></div>
                      </div>
                      <div className="progress-bar-container"><div className="progress-fill booked" style={{ width: (() => { const total = event.hasSeating ? event.totalSeats : event.standingCapacity; const booked = event.bookedTickets || 0; return total > 0 ? `${(booked / total) * 100}%` : '0%'; })() }}></div></div>
                    </div>

                    <div className="revenue-section">
                      <div className="revenue-row"><span>Stall Revenue</span><strong>₹{(event.stallRevenue || 0).toLocaleString()}</strong></div>
                      <div className="revenue-row"><span>Ticket Revenue</span><strong>₹{(event.ticketRevenue || 0).toLocaleString()}</strong></div>
                      <div className="revenue-total"><span>Total Revenue</span><strong>₹{(event.totalRevenue || 0).toLocaleString()}</strong></div>
                    </div>

                    <div className="budget-action-container">
                      <button className="btn-budget" onClick={() => { setSelectedBudgetEvent(selectedBudgetEvent === event.eventId ? null : event.eventId); }}><FontAwesomeIcon icon={faWallet} /> {selectedBudgetEvent === event.eventId ? 'Hide Budget' : 'Budget Planning'}</button>
                    </div>

                    {selectedBudgetEvent === event.eventId && (<BudgetPlanner event={event} onClose={() => setSelectedBudgetEvent(null)} onSave={() => { console.log("Budget saved/updated"); }} />)}

                    {expandedEvents[event.eventId] && selectedEvent?.eventId === event.eventId && (
                      <div className="details-container">
                        <div className="details-header">
                          <h3><FontAwesomeIcon icon={faListAlt} className="header-icon" /> Bookings Overview</h3>
                          <div className="booking-stats"><span className="stat-badge stall-badge"><FontAwesomeIcon icon={faStore} /> {stallBookings.length} Stalls</span><span className="stat-badge ticket-badge"><FontAwesomeIcon icon={faTicketAlt} /> {ticketBookings.length} Tickets</span></div>
                        </div>

                        {loadingDetails ? (
                          <div className="loading-state"><div className="spinner"></div><p>Loading booking details...</p></div>
                        ) : (
                          <>
                            {stallBookings && stallBookings.length > 0 ? (
                              <div className="bookings-section-wrapper">
                                <div className="section-label"><FontAwesomeIcon icon={faStore} className="label-icon" /><h4>Stall Bookings ({stallBookings.length})</h4></div>
                                <div className="bookings-grid three-column">
                                  {stallBookings.map((booking, idx) => (
                                    <div key={`stall-${idx}`} className="booking-card stall-card">
                                      <div className="card-header"><span className="item-number">Stall #{booking.stallNumber}</span><span className="status-badge">{booking.status || 'Booked'}</span></div>
                                      <div className="card-body">
                                        <div className="info-row"><FontAwesomeIcon icon={faUser} className="info-icon" /><span className="info-label">Customer:</span><span className="info-value" style={{ color: '#000' }}>{booking.customer?.name || 'Not recorded'}</span></div>
                                        <div className="info-row"><FontAwesomeIcon icon={faPhone} className="info-icon" /><span className="info-label">Phone:</span><span className="info-value" style={{ color: '#000' }}>{booking.customer?.phone || 'Not recorded'}</span></div>
                                        {booking.customer?.email && <div className="info-row"><FontAwesomeIcon icon={faEnvelope} className="info-icon" /><span className="info-label">Email:</span><span className="info-value" style={{ color: '#000' }}>{booking.customer.email}</span></div>}
                                        <div className="info-row"><FontAwesomeIcon icon={faCalendarCheck} className="info-icon" /><span className="info-label">Date:</span><span className="info-value" style={{ color: '#000' }}>{booking.bookingDate ? new Date(booking.bookingDate).toLocaleDateString() : formatDate(new Date())}</span></div>
                                        
                                        <div className="info-row">
                                          <span className="info-label">Original Price:</span>
                                          <span className="info-value" style={{ color: '#666', textDecoration: booking.discount > 0 ? 'line-through' : 'none' }}>
                                            ₹{parseFloat(booking.originalPrice).toLocaleString()}
                                          </span>
                                        </div>
                                        
                                        {booking.discount > 0 && (
                                          <div className="info-row">
                                            <span className="info-label">Discount:</span>
                                            <span className="info-value" style={{ color: '#dc3545' }}>- ₹{parseFloat(booking.discount).toLocaleString()}</span>
                                          </div>
                                        )}
                                        
                                        <div className="info-row price-row">
                                          <span className="info-label">Final Amount:</span>
                                          <span className="price-value" style={{ color: '#28a745', fontWeight: 'bold' }}>
                                            ₹{parseFloat(booking.finalPrice).toLocaleString()}
                                          </span>
                                        </div>
                                        
                                        {booking.stallDescription && (
                                          <div className="info-row" style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #e0e0e0' }}>
                                            <span className="info-label"><FontAwesomeIcon icon={faNoteSticky} /> Stall Notes:</span>
                                            <span className="info-value" style={{ color: '#6c757d', fontStyle: 'italic', fontSize: '0.7rem', display: 'block', marginTop: '4px' }}>
                                              {booking.stallDescription}
                                            </span>
                                          </div>
                                        )}
                                        
                                        {(booking.bookedBy || booking.bookedByPhone) && (
                                          <div className="info-row" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dotted #e0e0e0' }}>
                                            <span className="info-label"><FontAwesomeIcon icon={faUserPlus} /> Booked By:</span>
                                            <span className="info-value" style={{ color: '#6c757d', fontSize: '0.7rem' }}>
                                              {booking.bookedBy && `${booking.bookedBy}`}
                                              {booking.bookedBy && booking.bookedByPhone && ' - '}
                                              {booking.bookedByPhone && `${booking.bookedByPhone}`}
                                            </span>
                                          </div>
                                        )}
                                        
                                        <div className="payment-details-section" style={{ 
                                          marginTop: '10px', 
                                          paddingTop: '10px', 
                                          borderTop: '1px solid #e0e0e0',
                                          background: '#f8f9fa',
                                          borderRadius: '8px',
                                          padding: '10px',
                                          marginBottom: '10px'
                                        }}>
                                          <div className="payment-header" style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '6px', 
                                            marginBottom: '8px',
                                            fontSize: '0.7rem',
                                            fontWeight: '600',
                                            color: '#ffffff'
                                          }}>
                                            <FontAwesomeIcon icon={faCreditCard} /> PAYMENT INFO
                                          </div>
                                          
                                          {booking.paymentId && (
                                            <div className="info-row" style={{ fontSize: '0.7rem' }}>
                                              <span className="info-label">Payment ID:</span>
                                              <span className="info-value" style={{ color: '#2563eb', fontFamily: 'monospace', fontSize: '0.65rem' }}>
                                                {booking.paymentId}
                                              </span>
                                            </div>
                                          )}
                                          
                                          {booking.orderId && (
                                            <div className="info-row" style={{ fontSize: '0.7rem' }}>
                                              <span className="info-label">Order ID:</span>
                                              <span className="info-value" style={{ color: '#2563eb', fontFamily: 'monospace', fontSize: '0.65rem' }}>
                                                {booking.orderId}
                                              </span>
                                            </div>
                                          )}
                                          
                                          <div className="info-row" style={{ fontSize: '0.7rem' }}>
                                            <span className="info-label">Payment Method:</span>
                                            <span className="info-value" style={{ fontWeight: '500' }}>
                                              {booking.paymentMethod === 'RAZORPAY' ? '💳 Razorpay' : booking.paymentMethod === 'CASH' ? '💵 Cash' : booking.paymentMethod === 'UPI' ? '📱 UPI' : booking.paymentMethod}
                                            </span>
                                          </div>
                                          
                                          <div className="info-row" style={{ fontSize: '0.7rem' }}>
                                            <span className="info-label">Payment Status:</span>
                                            <span className="info-value" style={{ 
                                              color: booking.paymentStatus === 'PAID' ? '#28a745' : '#dc3545',
                                              fontWeight: '600'
                                            }}>
                                              {booking.paymentStatus === 'PAID' ? '✅ PAID' : booking.paymentStatus === 'PENDING' ? '⏳ PENDING' : booking.paymentStatus}
                                            </span>
                                          </div>
                                          
                                          {booking.receiptBookingTime && (
                                            <div className="info-row" style={{ fontSize: '0.7rem' }}>
                                              <span className="info-label">Booking Time:</span>
                                              <span className="info-value">
                                                {new Date(booking.receiptBookingTime).toLocaleString()}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                        
                                        {booking.paymentReceipt && (
                                          <div className="info-row receipt-row">
                                            <span className="info-label">Receipt:</span>
                                            <a href={`${API_BASE_URL}/../${booking.paymentReceipt}`} target="_blank" rel="noopener noreferrer" className="receipt-link" style={{ color: '#2563eb', textDecoration: 'underline' }}>
                                              <FontAwesomeIcon icon={faReceipt} /> View Receipt
                                            </a>
                                          </div>
                                        )}
                                        
                                        <div className="info-row action-row" style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                                          <button 
                                            className="edit-booking-btn" 
                                            onClick={() => handleEditBooking(booking)}
                                            style={{ background: '#2563eb', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.7rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', flex: 1, justifyContent: 'center' }}
                                          >
                                            <FontAwesomeIcon icon={faEdit} /> Edit
                                          </button>
                                          <button 
                                            className="cancel-booking-btn" 
                                            onClick={() => handleCancelStallBooking(event.eventId, booking.stallNumber, booking.customer?.name || 'Customer', booking.bookingId)} 
                                            style={{ background: '#dc2626', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.7rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', flex: 1, justifyContent: 'center' }}
                                          >
                                            <FontAwesomeIcon icon={faBan} /> Cancel
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (<div className="no-bookings-message"><p>No stall bookings found for this event</p></div>)}

                            {ticketBookings && ticketBookings.length > 0 ? (
                              <div className="bookings-section-wrapper">
                                <div className="section-label"><FontAwesomeIcon icon={faTicketAlt} className="label-icon" /><h4>Ticket Bookings ({ticketBookings.length})</h4></div>
                                <div className="bookings-grid three-column">
                                  {ticketBookings.map((booking, idx) => (
                                    <div key={`ticket-${idx}`} className="booking-card ticket-card">
                                      <div className="card-header"><span className="item-number">{booking.ticketType === 'standing' ? `${booking.quantity || 1}x General Admission` : `Seat #${booking.seatNo}`}</span><span className="status-badge">{booking.status || 'Booked'}</span></div>
                                      <div className="card-body">
                                        <div className="info-row"><FontAwesomeIcon icon={faUser} className="info-icon" /><span className="info-label">Customer:</span><span className="info-value" style={{ color: '#000' }}>{booking.customerName || 'N/A'}</span></div>
                                        <div className="info-row"><FontAwesomeIcon icon={faPhone} className="info-icon" /><span className="info-label">Phone:</span><span className="info-value" style={{ color: '#000' }}>{booking.phoneNumber || 'Not recorded'}</span></div>
                                        {booking.email && <div className="info-row"><FontAwesomeIcon icon={faEnvelope} className="info-icon" /><span className="info-label">Email:</span><span className="info-value" style={{ color: '#000' }}>{booking.email}</span></div>}
                                        <div className="info-row"><FontAwesomeIcon icon={faCalendarCheck} className="info-icon" /><span className="info-label">Date:</span><span className="info-value" style={{ color: '#000' }}>{booking.bookingTime ? new Date(booking.bookingTime).toLocaleDateString() : formatDate(new Date())}</span></div>
                                        <div className="info-row price-row"><span className="info-label">Amount:</span><span className="price-value" style={{ color: '#28a745', fontWeight: 'bold' }}>₹{booking.totalPrice?.toLocaleString() || booking.price?.toLocaleString() || '1,000'}{booking.quantity > 1 && ` (${booking.quantity} tickets)`}</span></div>
                                        {booking.ticketType === 'standing' && booking.quantity > 1 && (<div className="info-row"><span className="info-label">Ticket Type:</span><span className="info-value" style={{ color: '#000' }}>Standing ({booking.quantity} tickets)</span></div>)}
                                        <div className="info-row action-row">
                                          <button 
                                            className="cancel-booking-btn" 
                                            onClick={() => handleCancelTicketBooking(event.eventId, booking.bookingId, booking.customerName || 'Customer')} 
                                            style={{ background: '#dc2626', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.7rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', width: '100%', justifyContent: 'center' }}
                                          >
                                            <FontAwesomeIcon icon={faBan} /> Cancel Booking
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (<div className="no-bookings-message"><p>No ticket bookings found for this event</p></div>)}

                            {(!stallBookings || stallBookings.length === 0) && (!ticketBookings || ticketBookings.length === 0) && (<div className="empty-bookings"><FontAwesomeIcon icon={faExclamationTriangle} className="empty-icon" /><p>No bookings found for this event</p></div>)}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
      
      {view === "calendar" && (<div className="calendar-section"><Event_calander eventDetails={events} /></div>)}

      {view === "addEvent" && (
        <div className="addevent-section">
          <AddEvent onAddEvent={addEvent} />
          <div className="custom-layout-section">
            <button className="custom-layout-btn" onClick={handleNavigateToCustomLayout}>
              <FontAwesomeIcon icon={faCog} className="custom-layout-icon" />
              <div className="custom-layout-content">
                <h3>Design Custom Stall Layout</h3>
                <p>Create your own unique stall arrangement with drag-and-drop editor. Perfect for irregular venues or special setups.</p>
                <span className="btn-create-layout">Create Custom Layout →</span>
              </div>
              <div className="custom-layout-preview"><div className="preview-grid"><div className="preview-stall"></div><div className="preview-stall"></div><div className="preview-stall"></div><div className="preview-stall"></div></div></div>
            </button>
          </div>
        </div>
      )}

      {showEditModal && eventToEdit && (<EditEventModal event={eventToEdit} onClose={() => { setShowEditModal(false); setEventToEdit(null); }} onSave={handleSaveEditedEvent} />)}

      {showDeleteModal && eventToDelete && (
        <div className="modal-overlay">
          <div className="delete-modal">
            <div className="modal-header"><div className="warning-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div><h2>Delete Event</h2><button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button></div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>"{eventToDelete.eventName}"</strong>?</p>
              <div className="delete-warning"><FontAwesomeIcon icon={faExclamationTriangle} /><span>This action cannot be undone. All associated data will be permanently deleted:</span></div>
              <ul className="delete-items-list"><li>✓ Event details and configuration</li><li>✓ All stall bookings</li><li>✓ All ticket bookings</li><li>✓ Customer information</li><li>✓ Custom layout data (if any)</li><li>✓ Budget and expense records</li></ul>
              {eventToDelete.isCustomLayout && (<div className="custom-layout-warning"><FontAwesomeIcon icon={faCog} /><span>This is a custom layout event. All custom stall arrangements will be deleted.</span></div>)}
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowDeleteModal(false)} disabled={deleting}>Cancel</button>
              <button className="delete-btn" onClick={handleDeleteEvent} disabled={deleting}>{deleting ? (<><span className="spinner"></span> Deleting...</>) : (<><FontAwesomeIcon icon={faTrashAlt} /> Yes, Delete Event</>)}</button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Booking Confirmation Modal */}
      {showCancelModal && bookingToCancel && (
        <div className="modal-overlay">
          <div className="cancel-modal" style={{ background: 'white', borderRadius: '16px', maxWidth: '450px', width: '90%', overflow: 'hidden' }}>
            <div className="modal-header" style={{ padding: '1.5rem', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '1rem', position: 'relative' }}>
              <div className="warning-icon cancel-icon" style={{ width: '50px', height: '50px', background: '#dc2626', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: 'white' }}><FontAwesomeIcon icon={faBan} /></div>
              <h2 style={{ margin: 0, fontSize: '1.3rem', color: '#333' }}>Cancel Booking</h2>
              <button className="modal-close" onClick={() => setShowCancelModal(false)} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#999' }}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '1.5rem' }}>
              <p style={{ color: '#333' }}>Are you sure you want to cancel this booking?</p>
              <div className="booking-details" style={{ background: '#f3f4f6', padding: '12px', borderRadius: '8px', margin: '15px 0' }}>
                <p style={{ margin: '5px 0', color: '#333' }}><strong>Customer:</strong> {bookingToCancel.customerName}</p>
                <p style={{ margin: '5px 0', color: '#333' }}><strong>Type:</strong> {bookingToCancel.type === 'stall' ? 'Stall Booking' : 'Ticket Booking'}</p>
                {bookingToCancel.type === 'stall' && (<p style={{ margin: '5px 0', color: '#333' }}><strong>Stall Number:</strong> {bookingToCancel.stallNumber}</p>)}
              </div>
              <div className="cancel-warning" style={{ background: '#fef2f2', borderLeft: '4px solid #dc2626', padding: '12px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.85rem', color: '#991b1b' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} /><span>This action cannot be undone. The booking will be permanently removed.</span>
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '1rem 1.5rem 1.5rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button className="cancel-btn" onClick={() => setShowCancelModal(false)} disabled={cancelling} style={{ padding: '0.7rem 1.5rem', background: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Keep Booking</button>
              <button className="cancel-confirm-btn" onClick={confirmCancelBooking} disabled={cancelling} style={{ padding: '0.7rem 1.5rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {cancelling ? (<><span className="spinner" style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></span> Cancelling...</>) : (<><FontAwesomeIcon icon={faBan} /> Yes, Cancel Booking</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Booking Modal */}
      {showEditBookingModal && bookingToEdit && (
        <div className="modal-overlay" onClick={() => setShowEditBookingModal(false)}>
          <div className="edit-booking-modal" onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '20px', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflow: 'auto', position: 'relative' }}>
            <div className="modal-header" style={{ padding: '1.5rem', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', color: 'white', borderRadius: '20px 20px 0 0' }}>
              <h2 style={{ margin: 0, fontSize: '1.3rem' }}>
                <FontAwesomeIcon icon={faEdit} style={{ marginRight: '10px' }} />
                Edit Booking #{bookingToEdit.bookingId}
              </h2>
              <button className="modal-close" onClick={() => setShowEditBookingModal(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', width: '32px', height: '32px', borderRadius: '50%', color: 'white', cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body" style={{ padding: '1.5rem' }}>
              <div className="form-section" style={{ marginBottom: '20px' }}>
                <h3 style={{ marginBottom: '15px', color: '#333', fontSize: '1rem' }}>
                  <FontAwesomeIcon icon={faUser} style={{ marginRight: '8px', color: '#b8860b' }} />
                  Customer Details
                </h3>
                
                <div className="form-group" style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Customer Name *</label>
                  <input type="text" className="form-control" value={editFormData.customerName} onChange={(e) => setEditFormData({...editFormData, customerName: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px' }} />
                </div>
                
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input type="tel" className="form-control" value={editFormData.customerPhone} onChange={(e) => setEditFormData({...editFormData, customerPhone: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px' }} />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" className="form-control" value={editFormData.customerEmail} onChange={(e) => setEditFormData({...editFormData, customerEmail: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px' }} />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Company</label>
                  <input type="text" className="form-control" value={editFormData.customerCompany} onChange={(e) => setEditFormData({...editFormData, customerCompany: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px' }} />
                </div>
                
                <div className="form-group">
                  <label>Requirements</label>
                  <textarea className="form-control" value={editFormData.customerRequirements} onChange={(e) => setEditFormData({...editFormData, customerRequirements: e.target.value})} rows="3" style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px' }} />
                </div>
              </div>
              
              <div className="form-section" style={{ marginBottom: '20px' }}>
                <h3 style={{ marginBottom: '15px', color: '#333', fontSize: '1rem' }}>
                  <FontAwesomeIcon icon={faStore} style={{ marginRight: '8px', color: '#b8860b' }} />
                  Stall & Payment Details
                </h3>
                
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Stall Number</label>
                    <input type="number" className="form-control" value={editFormData.stallNumber} onChange={(e) => setEditFormData({...editFormData, stallNumber: parseInt(e.target.value)})} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px' }} />
                  </div>
                  <div className="form-group">
                    <label>Original Price (₹)</label>
                    <input type="number" className="form-control" value={editFormData.stallPrice} onChange={(e) => setEditFormData({...editFormData, stallPrice: parseFloat(e.target.value)})} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px' }} />
                  </div>
                </div>
                
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Discount (₹)</label>
                    <input type="number" className="form-control" value={editFormData.discount} onChange={handleEditDiscountChange} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px' }} />
                  </div>
                  <div className="form-group">
                    <label>Final Price (₹)</label>
                    <input type="number" className="form-control" value={editFormData.finalPrice} readOnly style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', background: '#f5f5f5' }} />
                  </div>
                </div>
                
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Payment Method</label>
                    <select className="form-control" value={editFormData.paymentMethod} onChange={(e) => setEditFormData({...editFormData, paymentMethod: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px' }}>
                      <option value="CASH">💵 Cash</option>
                      <option value="ONLINE">💳 Online Payment</option>
                      <option value="CARD">💳 Card Swipe</option>
                      <option value="UPI">📱 UPI</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Payment Status</label>
                    <select className="form-control" value={editFormData.paymentStatus} onChange={(e) => setEditFormData({...editFormData, paymentStatus: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px' }}>
                      <option value="PAID">✅ Paid</option>
                      <option value="PENDING">⏳ Pending</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Booking Status</label>
                  <select className="form-control" value={editFormData.bookingStatus} onChange={(e) => setEditFormData({...editFormData, bookingStatus: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px' }}>
                    <option value="CONFIRMED">✅ Confirmed</option>
                    <option value="PENDING">⏳ Pending</option>
                    <option value="CANCELLED">❌ Cancelled</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="modal-footer" style={{ padding: '1rem 1.5rem 1.5rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end', borderTop: '1px solid #e0e0e0' }}>
              <button className="cancel-btn" onClick={() => setShowEditBookingModal(false)} disabled={editingBooking} style={{ padding: '10px 20px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button className="save-btn" onClick={saveEditedBooking} disabled={editingBooking} style={{ padding: '10px 20px', background: '#b8860b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {editingBooking ? (<><span className="spinner" style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></span> Saving...</>) : (<><FontAwesomeIcon icon={faSave} /> Save Changes</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;